import { apiClient } from '@/lib/api-client'
import type { JobType } from '@/types'
import type { ProgramType } from '@/types/access-control'

// Types
/** Entitlements the backend is willing to expose publicly (PUBLIC_FEATURE_KEYS). */
export interface PortalOrgFeatures {
  hris?: boolean
  /** Presentation-only, not a second access gate -- see FEATURE_HRIS_REP_MEDIATED. */
  hris_rep_mediated?: boolean
  severance?: boolean
}

export interface PortalOrganizationInfo {
  /** Whitelisted entitlements only; the backend filters this endpoint. */
  features?: PortalOrgFeatures
  slug: string
  name: string
  logo_url: string | null
}

export interface PortalEmployee {
  id: number
  first_name: string | null
  last_name: string | null
  email: string | null
  phone_number: string | null
  location: string | null
  seniority: string | null
  department: string | null
  manager_id: number | null
  manager_name: string | null
  manager_title: string | null
  is_active: boolean
  onboarding_status: string | null
  program_type: ProgramType | null
  workflow_state: string | null
  invited_at: string | null
  created_at: string
  is_hris_managed: boolean
  removed_at: string | null
}

export interface PortalEmployeeList {
  employees: PortalEmployee[]
  total: number
  organization_total: number
  page: number
  limit: number
  pages: number
  departments: { value: string; count: number }[]
  roles: string[]
}

export interface ParticipantProgramAssignment {
  id: number
  user_id: number
  organization_id: number
  cohort_id: number
  journey_type: string
  program_type: ProgramType
  status: string
}

export interface PortalProgramTemplate {
  program_type: ProgramType
  label: string
  description: string
  included_features: string[]
  excluded_features: string[]
  requires_severance_release: boolean
}

export interface PortalProgramDetail {
  user_id: number
  program: {
    id: number
    program_type: ProgramType
    status: string
    starts_on: string | null
    ends_on: string | null
  } | null
  workflow_state: string | null
  workflow_restricted: boolean
}

export interface PortalProgramHistoryItem {
  id: number
  action: string
  actor_user_id: number | null
  reason: string
  metadata: Record<string, unknown>
  created_at: string
}

export interface PortalImportResult {
  row: number
  success: boolean
  user_id: number | null
  error: string | null
}

export interface PortalImportResponse {
  import_id: number
  total: number
  success_count: number
  failure_count: number
  results: PortalImportResult[]
  warnings: string[]
}

/** "people" creates/updates people; "severance" sets terms on people who
 * already exist. See ImportHistory.import_kind (sevadmin09). */
export type PortalImportKind = 'people' | 'severance'

export interface PortalImportHistoryItem {
  id: number
  original_filename: string
  file_type: string
  total_rows: number
  success_count: number
  failure_count: number
  created_at: string
  uploaded_by_name: string | null
  import_kind: PortalImportKind
}

export interface PortalDashboardStats {
  total_employees: number
  active_employees: number
  total_imports: number
  recent_imports: PortalImportHistoryItem[]
}

export interface PortalEmployeeListParams {
  page?: number
  limit?: number
  search?: string
  department?: string
  role?: string
  /** Show only removed people instead of only current ones. */
  include_removed?: boolean
}

export type InternalJobStatus = 'open' | 'closed' | 'all'

export interface InternalJobPosting {
  id: number
  organization_id: number
  requisition_id: string | null
  title: string
  department: string | null
  organization_department_id?: string | null
  needs_department_mapping?: boolean
  location: string | null
  description: string
  job_type: JobType | null
  skills: string[]
  is_open: boolean
  employee_visible: boolean
  published_at: string | null
  closes_at: string | null
  application_url: string | null
  created_at: string
  updated_at: string
}

export interface InternalJobPostingInput {
  requisition_id?: string | null
  title: string
  department?: string | null
  location?: string | null
  description: string
  job_type?: JobType | null
  skills?: string[]
  is_open?: boolean
  employee_visible?: boolean
  published_at?: string | null
  closes_at?: string | null
  application_url?: string | null
}

export interface InternalJobPostingList {
  jobs: InternalJobPosting[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface InternalJobImportRow {
  row: number
  success: boolean
  requisition_id: string | null
  title: string | null
  action: 'create' | 'update' | null
  job_id: number | null
  error: string | null
}

export interface InternalJobImportPreview {
  filename: string
  total: number
  valid_count: number
  invalid_count: number
  will_create: number
  will_update: number
  results: InternalJobImportRow[]
}

export interface InternalJobImportResponse {
  total: number
  success_count: number
  failure_count: number
  created_count: number
  updated_count: number
  results: InternalJobImportRow[]
}

export interface InternalJobCandidateMatch {
  employee_id: number
  employee_name: string
  email: string | null
  current_title: string | null
  current_department: string | null
  resume_document_id: number
  resume_filename: string
  resume_updated_at: string
  manager_id: number | null
  manager_name: string | null
  manager_title: string | null
  overall_score: number
  skills_score: number
  experience_score: number
  similarity: number
  matched_skills: string[]
  missing_skills: string[]
  match_summary: string
  match_basis: 'resume_and_hris'
}

export interface InternalJobCandidateList {
  job: InternalJobPosting
  matches: InternalJobCandidateMatch[]
  total: number
  ranked_total: number
  total_employees: number
  excluded_no_resume_count: number
  excluded_missing_vector_count: number
  available_departments: string[]
  page: number
  limit: number
  pages: number
}

export interface EmployeeInternalJobMatch {
  job: InternalJobPosting
  overall_score: number
  skills_score: number
  experience_score: number
  similarity: number
  matched_skills: string[]
  missing_skills: string[]
  match_summary: string
  match_basis: 'resume_and_hris'
}

export interface EmployeeInternalJobMatchList {
  employee_id: number
  employee_name: string
  current_title: string | null
  current_department: string | null
  manager_id: number | null
  manager_name: string | null
  manager_title: string | null
  status: 'ranked' | 'no_resume' | 'embedding_pending'
  message: string | null
  resume_document_id: number | null
  resume_updated_at: string | null
  matches: EmployeeInternalJobMatch[]
  total: number
  page: number
  limit: number
  pages: number
}

export interface InternalJobCandidateFilters {
  search?: string
  department?: string
  min_score?: number
}

// API functions
export async function getOrgBranding(slug: string): Promise<PortalOrganizationInfo> {
  const response = await apiClient.get<PortalOrganizationInfo>(`/api/v1/portal/org/${slug}`)
  return response.data
}

export async function getPortalDashboard(): Promise<PortalDashboardStats> {
  const response = await apiClient.get<PortalDashboardStats>('/api/v1/portal/dashboard')
  return response.data
}

export async function getPortalEmployees(
  params: PortalEmployeeListParams = {},
): Promise<PortalEmployeeList> {
  const response = await apiClient.get<PortalEmployeeList>('/api/v1/portal/employees', { params })
  return response.data
}

export async function removePortalEmployee(employeeId: number): Promise<PortalEmployee> {
  const response = await apiClient.post<PortalEmployee>(
    `/api/v1/portal/employees/${employeeId}/remove`,
  )
  return response.data
}

export async function restorePortalEmployee(employeeId: number): Promise<PortalEmployee> {
  const response = await apiClient.post<PortalEmployee>(
    `/api/v1/portal/employees/${employeeId}/restore`,
  )
  return response.data
}

export async function getInternalJobs(
  params: {
    status?: InternalJobStatus
    search?: string
    page?: number
    limit?: number
  } = {},
): Promise<InternalJobPostingList> {
  const response = await apiClient.get<InternalJobPostingList>('/api/v1/portal/internal-jobs', {
    params,
  })
  return response.data
}

export async function createInternalJob(
  payload: InternalJobPostingInput,
): Promise<InternalJobPosting> {
  const response = await apiClient.post<InternalJobPosting>('/api/v1/portal/internal-jobs', payload)
  return response.data
}

export async function updateInternalJob(
  jobId: number,
  payload: Partial<InternalJobPostingInput>,
): Promise<InternalJobPosting> {
  const response = await apiClient.patch<InternalJobPosting>(
    `/api/v1/portal/internal-jobs/${jobId}`,
    payload,
  )
  return response.data
}

export async function getInternalJobCandidates(
  jobId: number,
  params: InternalJobCandidateFilters & { page?: number; limit?: number } = {},
): Promise<InternalJobCandidateList> {
  const response = await apiClient.get<InternalJobCandidateList>(
    `/api/v1/portal/internal-jobs/${jobId}/candidates`,
    { params },
  )
  return response.data
}

export async function getEmployeeInternalJobMatches(
  employeeId: number,
  page = 1,
  limit = 10,
): Promise<EmployeeInternalJobMatchList> {
  const response = await apiClient.get<EmployeeInternalJobMatchList>(
    `/api/v1/portal/employees/${employeeId}/internal-job-matches`,
    { params: { page, limit } },
  )
  return response.data
}

export async function previewInternalJobImport(file: File): Promise<InternalJobImportPreview> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<InternalJobImportPreview>(
    '/api/v1/portal/internal-jobs/import/preview',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

export async function importInternalJobs(file: File): Promise<InternalJobImportResponse> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<InternalJobImportResponse>(
    '/api/v1/portal/internal-jobs/import',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

export async function importPortalEmployees(file: File): Promise<PortalImportResponse> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<PortalImportResponse>('/api/v1/portal/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

/** Set severance terms from a file. Every row must match a person who
 * already exists in this org (by email) -- this never creates anyone. */
export async function importSeveranceTerms(file: File): Promise<PortalImportResponse> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<PortalImportResponse>(
    '/api/v1/portal/severance/import',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

/** One negotiated term a row would change, old value to new. Dates arrive as
 * ISO strings (YYYY-MM-DD); money and percentage fields as numbers. */
export interface SeveranceTermChange {
  term: string
  old_value: number | string | null
  new_value: number | string | null
}

export interface PortalImportPreviewRow {
  row: number
  success: boolean
  error: string | null
  email: string | null
  name: string | null
  employee_id: string | null
  /** "create" adds a person, "update" revises one who already exists.
   * Severance-terms rows are always "update" -- that import never creates
   * anyone. */
  action: 'create' | 'update' | null
  /** Terms this row explicitly clears ("-", "none", ...). Severance preview only. */
  cleared_terms?: string[]
  /** Money/date/percentage terms this row would actually change, old value to
   * new. Only entries where the value actually differs -- empty for a row
   * that touches no negotiated term. Severance preview only. */
  term_changes?: SeveranceTermChange[]
}

export interface PortalImportPreview {
  filename: string
  total: number
  valid_count: number
  invalid_count: number
  will_create: number
  will_update: number
  warnings: string[]
  results: PortalImportPreviewRow[]
}

/** Dry run. Writes nothing -- no users, no import history. Base columns only. */
export async function previewPortalImport(file: File): Promise<PortalImportPreview> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<PortalImportPreview>(
    '/api/v1/portal/import/preview',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

/** Dry run for a severance-terms upload. Writes nothing. A row whose person
 * cannot be found in this org comes back as an error, same as the real import. */
export async function previewSeveranceImport(file: File): Promise<PortalImportPreview> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<PortalImportPreview>(
    '/api/v1/portal/severance/import/preview',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return response.data
}

/** ``kind`` filters to "people" or "severance"; omitted returns both. */
export async function getPortalImportHistory(
  kind?: PortalImportKind,
): Promise<PortalImportHistoryItem[]> {
  const response = await apiClient.get<PortalImportHistoryItem[]>('/api/v1/portal/imports', {
    params: kind ? { kind } : undefined,
  })
  return response.data
}

export async function getPortalImportDownloadUrl(
  importId: number,
): Promise<{ download_url: string; filename: string }> {
  const response = await apiClient.get<{ download_url: string; filename: string }>(
    `/api/v1/portal/imports/${importId}/download`,
  )
  return response.data
}

export async function invitePortalEmployee(
  employeeId: number,
): Promise<{ message: string; employee_id: number; stakeholder_notifications_queued: number }> {
  const response = await apiClient.post<{
    message: string
    employee_id: number
    stakeholder_notifications_queued: number
  }>(`/api/v1/portal/employees/${employeeId}/invite`)
  return response.data
}

export async function assignPortalEmployeeProgram(
  employeeId: number,
  programType: ProgramType,
  reason: string,
): Promise<ParticipantProgramAssignment> {
  const response = await apiClient.put<ParticipantProgramAssignment>(
    `/api/v1/portal/people/${employeeId}/program`,
    { program_type: programType, reason },
  )
  return response.data
}

export async function getPortalProgramTemplates(): Promise<PortalProgramTemplate[]> {
  const response = await apiClient.get<PortalProgramTemplate[]>(
    '/api/v1/portal/participant-programs/templates',
  )
  return response.data
}

export async function getPortalEmployeeProgram(employeeId: number): Promise<PortalProgramDetail> {
  const response = await apiClient.get<PortalProgramDetail>(
    `/api/v1/portal/people/${employeeId}/program`,
  )
  return response.data
}

export async function getPortalEmployeeProgramHistory(
  employeeId: number,
): Promise<PortalProgramHistoryItem[]> {
  const response = await apiClient.get<PortalProgramHistoryItem[]>(
    `/api/v1/portal/people/${employeeId}/program/history`,
  )
  return response.data
}

/** Downloads a blob response and triggers a browser save-as under `filename`. */
function saveCsvBlob(data: BlobPart, filename: string): void {
  const blob = new Blob([data], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/** Blank people-import template. Base columns only -- severance terms have
 * their own worksheet (downloadSeveranceImportTemplate). */
export async function downloadPortalImportTemplate(): Promise<void> {
  const response = await apiClient.get('/api/v1/portal/import/template', { responseType: 'blob' })
  saveCsvBlob(response.data, 'employee_import_template.csv')
}

/** Severance-terms worksheet: every current employee with the terms they
 * already carry, ready to fill in and re-upload. */
export async function downloadSeveranceImportTemplate(): Promise<void> {
  const response = await apiClient.get('/api/v1/portal/severance/import/template', {
    responseType: 'blob',
  })
  saveCsvBlob(response.data, 'severance_terms_worksheet.csv')
}

export async function downloadInternalJobsTemplate(): Promise<void> {
  const response = await apiClient.get('/api/v1/portal/internal-jobs/import/template', {
    responseType: 'blob',
  })
  saveCsvBlob(response.data, 'internal_jobs_template.csv')
}

export interface PortalElectionRow {
  package_id: number
  payroll_id: string | null
  first_name: string | null
  last_name: string | null
  email: string | null
  employee_id: string
  lump_sum_amount: number | null
  continuation_rate: number | null
  continuation_duration_months: number | null
  severance_start_date: string | null
  balloon_payout_pct: number | null
  /** HRIS-owned: the sync rewrites these, the roster file can override them. */
  salary_basis: number | null
  department: string | null
  years_of_service: number | null
  work_state: string | null
  choice: 'lump_sum' | 'continuation' | null
  status: 'open' | 'locked' | 'stopped' | 'paid_out' | null
  is_default: boolean
  responded: boolean
  elected_at: string | null
  locked_at: string | null
  reemployment_reported_at: string | null
  /** Whether they can sign in at all — an unclaimed account cannot respond. */
  onboarding_status: string | null
  can_claim: boolean
}

export interface PortalElectionsSummary {
  stopped: number
  unclaimed: number
  total: number
  responded: number
  not_responded: number
  open: number
  locked: number
  lump_sum: number
  continuation: number
}

export interface PortalElectionsResponse {
  rows: PortalElectionRow[]
  summary: PortalElectionsSummary
  election_deadline: string | null
  total: number
  page: number
  limit: number
  pages: number
}

export async function getBenefitsElections(
  params: {
    page?: number | undefined
    limit?: number | undefined
    search?: string | undefined
    sort?: string | undefined
    direction?: 'asc' | 'desc' | undefined
  } = {},
): Promise<PortalElectionsResponse> {
  const search = params.search?.trim()
  const response = await apiClient.get<PortalElectionsResponse>(
    '/api/v1/portal/benefits/elections',
    {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 50,
        // Omitted rather than sent empty, so the default ordering stays the
        // server's business.
        ...(search ? { search } : {}),
        ...(params.sort ? { sort: params.sort, direction: params.direction ?? 'desc' } : {}),
      },
    },
  )
  return response.data
}

export async function downloadBenefitsElectionsCsv(): Promise<void> {
  const response = await apiClient.get('/api/v1/portal/benefits/elections.csv', {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(response.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'elections.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

export interface EmployeeTermsUpdate {
  lump_sum_amount?: number | null
  salary_basis?: number | null
  continuation_rate?: number | null
  continuation_duration_months?: number | null
  severance_start_date?: string | null
  balloon_payout_pct?: number
}

export interface CohortDefaults {
  continuation_rate: number
  continuation_duration_months: number
  severance_start_date: string
  // Optional, unlike the three fields above: omitting it leaves each
  // package's existing balloon_payout_pct alone rather than clobbering
  // per-employee overrides every time other cohort defaults are reapplied.
  balloon_payout_pct?: number
}

export interface ApplyDefaultsResult {
  applied: number
  skipped_locked: number
}

export interface CohortSettingsUpdate {
  election_deadline: string | null
  election_timezone: string
  /**
   * Cohort-level severance policy, decided once for the whole layoff. The
   * employee decision aid is inert without these: health coverage is its
   * strongest factor in every state, first_payment_date drives the New York
   * 30-day rule, and characterization is what matters in California.
   */
  first_payment_date?: string | null
  health_coverage_continues?: boolean | null
  health_coverage_months?: number | null
  severance_characterization?: string | null
}

export async function updateEmployeeTerms(
  packageId: number,
  terms: EmployeeTermsUpdate,
): Promise<PortalElectionRow> {
  const response = await apiClient.patch<PortalElectionRow>(
    `/api/v1/portal/benefits/packages/${packageId}`,
    terms,
  )
  return response.data
}

export async function applyCohortDefaults(defaults: CohortDefaults): Promise<ApplyDefaultsResult> {
  const response = await apiClient.post<ApplyDefaultsResult>(
    '/api/v1/portal/benefits/apply-defaults',
    defaults,
  )
  return response.data
}

export async function updateCohortSettings(
  settings: CohortSettingsUpdate,
): Promise<CohortSettingsUpdate> {
  const response = await apiClient.patch<CohortSettingsUpdate>(
    '/api/v1/portal/benefits/cohort',
    settings,
  )
  return response.data
}

export type CohortSettings = CohortSettingsUpdate

/** Current cohort settings (FE-03) -- loaded before the settings dialog
 * renders, so a save that only touches the deadline does not also overwrite
 * policy the dialog never saw. */
export async function getCohortSettings(): Promise<CohortSettings> {
  const response = await apiClient.get<CohortSettings>('/api/v1/portal/benefits/cohort')
  return response.data
}

export interface PortalMatchReport {
  id: number
  employee_number: string | null
  // Mirrors app/models/hris_match_report.py MatchReason.
  reason:
    | 'roster_only'
    | 'hris_only'
    | 'departed'
    | 'duplicate'
    | 'ambiguous'
    | 'email_exists_elsewhere'
    | 'provider_record_rejected'
  detail: Record<string, unknown> | null
  created_at: string
  resolved_at: string | null
  /** The person this is about. Null when nothing in People matches. */
  user_id: number | null
  name: string | null
  email: string | null
}

export interface PortalMatchReports {
  total: number
  counts: Record<string, number>
  reports: PortalMatchReport[]
}

/** Rows the HRIS and the severance roster disagree about. Unresolved by default. */
export async function getPortalMatchReports(includeResolved = false): Promise<PortalMatchReports> {
  const response = await apiClient.get<PortalMatchReports>('/api/v1/portal/match-reports', {
    params: includeResolved ? { include_resolved: true } : undefined,
  })
  return response.data
}

/** The endpoint only echoes back id and resolved_at, not the full report --
 * typing this as PortalMatchReport would let a caller read .reason/.detail/
 * .employee_number off a response that never carries them. */
export async function resolvePortalMatchReport(
  reportId: number,
  resolved = true,
): Promise<Pick<PortalMatchReport, 'id' | 'resolved_at'>> {
  const response = await apiClient.patch<Pick<PortalMatchReport, 'id' | 'resolved_at'>>(
    `/api/v1/portal/match-reports/${reportId}`,
    null,
    { params: { resolved } },
  )
  return response.data
}

export interface DemoResetResult {
  message: string
}

/** Resets the Delgo demo tenant's seeded users/packages so a live demo can be
 * replayed from the portal. Server-gated (superuser, non-production, delgo
 * slug only) -- see app/api/v1/endpoints/admin_demo.py. */
export async function resetDemoTenant(): Promise<DemoResetResult> {
  const response = await apiClient.post<DemoResetResult>('/api/v1/admin/demo/reset')
  return response.data
}
